import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Archive as ArchiveIcon,
  Banknote,
  BarChart3,
  BookOpen,
  Calculator,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  Cog,
  Eye,
  FileText,
  FileStack,
  Folder,
  FolderOpen,
  Home,
  Inbox,
  LayoutDashboard,
  LayoutTemplate,
  ListChecks,
  ListTodo,
  LogOut,
  MessageSquare,
  Network,
  PiggyBank,
  Plus,
  Receipt,
  Scale,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  TrendingDown,
  Wallet,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import { Button, Dialog, LanguageToggle, ThemeToggle, cn } from '@solara/ui-shared';
import { RoleSwitcher } from '../auth/RoleSwitcher';
import { useAuth } from '../auth/AuthContext';
import { useSession } from '../auth/SessionContext';
import { NotificationsButton } from '../components/NotificationsButton';
import type { PortalCapability } from '../auth/AuthContext';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Capability gate; if user lacks it, hide the entry. */
  cap?: PortalCapability;
}

interface NavSection {
  /** Stable id used as the expand/collapse key — must not change with locale. */
  id: string;
  title: string;
  /** Primary route the section header links to. */
  to: string;
  icon: React.ReactNode;
  items: NavItem[];
}

const COLLAPSE_KEY = 'solara.portal.sidebarCollapsed';

export default function PortalShell() {
  const { t } = useI18n();
  const { t: tP1 } = useTranslation('p1');
  const { t: tLogin } = useTranslation('login');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const session = useSession();
  const [moreOpen, setMoreOpen] = useState(false);
  // Prefer the real signed-in user from SessionContext for display; fall back
  // to sample-data only for the role-switcher demo persona until /auth/me
  // resolves. See [[solara-tickstar-swap]] — display identity must match the
  // JWT, not the demo-mode AuthContext sample list.
  const displayName = session.user
    ? `${session.user.givenName} ${session.user.familyName}`.trim() || session.user.email
    : `${auth.currentUser.givenName} ${auth.currentUser.familyName}`;
  const displayEmail = session.user?.email ?? auth.currentUser.email;
  const displayInitial = (session.user?.givenName?.[0] ?? auth.currentUser.givenName?.[0] ?? 'U').toUpperCase();

  function handleSignOut() {
    session.signOut();
    navigate('/login', { replace: true });
  }
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Two-level navigation: 8 top-level sections, each with 1+ sub-routes.
  // Section header is itself a link (to the first relevant route) and also
  // toggles expand/collapse of its children. Icon-uniqueness invariant:
  // each lucide icon appears at most once across the whole nav.
  const sections: NavSection[] = [
    {
      id: 'dashboard',
      title: tP1('customerSections.dashboard'),
      to: '/',
      icon: <LayoutDashboard className="h-5 w-5" />,
      items: [
        { to: '/',              label: tP1('nav.overview'),     icon: <Eye className="h-4 w-4" /> },
        { to: '/tax/dashboard', label: tP1('nav.taxDashboard'), icon: <BarChart3 className="h-4 w-4" /> },
      ],
    },
    {
      id: 'invoicing',
      title: tP1('customerSections.invoicing'),
      to: '/invoices',
      icon: <FileStack className="h-5 w-5" />,
      items: [
        { to: '/invoices',  label: t('nav.invoices'),    icon: <FileText className="h-4 w-4" /> },
        { to: '/new',       label: t('nav.newInvoice'),  icon: <Plus className="h-4 w-4" />, cap: 'invoices.create' },
        { to: '/templates', label: t('nav.templates'),   icon: <LayoutTemplate className="h-4 w-4" />, cap: 'invoices.create' },
        { to: '/inbox',     label: t('nav.smartInbox'),  icon: <Inbox className="h-4 w-4" />, cap: 'inbox.review' },
        { to: '/archive',   label: t('nav.archive'),     icon: <ArchiveIcon className="h-4 w-4" />, cap: 'invoices.archive.search' },
      ],
    },
    {
      id: 'approvalsTasks',
      title: tP1('customerSections.approvalsTasks'),
      to: '/approvals',
      icon: <ListTodo className="h-5 w-5" />,
      items: [
        { to: '/approvals', label: t('nav.approvals'),  icon: <CheckSquare className="h-4 w-4" />, cap: 'invoices.approve' },
        { to: '/my-tasks',  label: tP1('nav.myTasks'),  icon: <ListChecks className="h-4 w-4" /> },
      ],
    },
    {
      id: 'expensesAudit',
      title: tP1('customerSections.expensesAudit'),
      to: '/spend',
      icon: <Wallet className="h-5 w-5" />,
      items: [
        { to: '/spend',          label: t('nav.spend'),         icon: <PiggyBank className="h-4 w-4" />,    cap: 'spend.read' },
        { to: '/recovery-audit', label: t('nav.recoveryAudit'), icon: <Search className="h-4 w-4" />,       cap: 'recovery_audit.read' },
        { to: '/finance/aging',  label: tP1('nav.aging'),       icon: <TrendingDown className="h-4 w-4" />, cap: 'spend.read' },
      ],
    },
    {
      id: 'taxCompliance',
      title: tP1('customerSections.taxCompliance'),
      to: '/compliance',
      icon: <Scale className="h-5 w-5" />,
      items: [
        { to: '/compliance',          label: t('nav.compliance'),  icon: <ShieldCheck className="h-4 w-4" />, cap: 'compliance.read' },
        { to: '/tax/filings',         label: tP1('nav.taxFilings'), icon: <ScrollText className="h-4 w-4" /> },
        { to: '/vat',                 label: t('nav.vat'),         icon: <Receipt className="h-4 w-4" />,     cap: 'vat.read' },
        { to: '/peppol-registration', label: t('nav.peppolRegistration', { defaultValue: 'Peppol registration' }), icon: <Network className="h-4 w-4" />, cap: 'compliance.read' },
      ],
    },
    {
      id: 'financeBookkeeping',
      title: tP1('customerSections.financeBookkeeping'),
      to: '/banking',
      icon: <Calculator className="h-5 w-5" />,
      items: [
        { to: '/banking',    label: tP1('nav.banking'),    icon: <Banknote className="h-4 w-4" />, cap: 'spend.read' },
        { to: '/accounting', label: tP1('nav.accounting'), icon: <BookOpen className="h-4 w-4" />, cap: 'spend.read' },
      ],
    },
    {
      id: 'docsCommunication',
      title: tP1('customerSections.docsCommunication'),
      to: '/documents',
      icon: <FolderOpen className="h-5 w-5" />,
      items: [
        { to: '/documents', label: tP1('nav.documents'), icon: <Folder className="h-4 w-4" /> },
        { to: '/chat',      label: tP1('nav.chat'),      icon: <MessageSquare className="h-4 w-4" /> },
      ],
    },
    {
      id: 'system',
      title: tP1('customerSections.system'),
      to: '/settings',
      icon: <Cog className="h-5 w-5" />,
      items: [
        { to: '/settings', label: t('nav.settings'), icon: <SettingsIcon className="h-4 w-4" /> },
      ],
    },
  ];

  // Filter capability-gated items, then drop empty sections.
  const visibleSections = useMemo(
    () =>
      sections
        .map((s) => ({ ...s, items: s.items.filter((it) => !it.cap || auth.can(it.cap)) }))
        .filter((s) => s.items.length > 0),
    [sections, auth],
  );

  // Auto-expand the section that contains the active route. Subsequent
  // manual toggles are tracked in `expanded`. The Set stores section ids
  // that the user has opened *or* that we've auto-opened.
  const activeSectionId = useMemo(() => {
    for (const s of visibleSections) {
      if (s.items.some((it) => isRouteActive(location.pathname, it.to))) return s.id;
    }
    return visibleSections[0]?.id ?? null;
  }, [visibleSections, location.pathname]);

  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(activeSectionId ? [activeSectionId] : []),
  );

  useEffect(() => {
    if (!activeSectionId) return;
    setExpanded((prev) => {
      if (prev.has(activeSectionId)) return prev;
      const next = new Set(prev);
      next.add(activeSectionId);
      return next;
    });
  }, [activeSectionId]);

  function toggleSection(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // 4-tab mobile bottom nav: Home, Invoices, +, More. Use the first match
  // across visible sections so capability-filtering doesn't break it.
  const allVisibleItems = visibleSections.flatMap((s) => s.items);
  const bottomItems = [
    allVisibleItems.find((i) => i.to === '/') ?? allVisibleItems[0],
    allVisibleItems.find((i) => i.to === '/invoices'),
    allVisibleItems.find((i) => i.to === '/new'),
  ].filter(Boolean) as NavItem[];

  return (
    <div
      className={cn(
        'min-h-full bg-bg flex flex-col lg:grid',
        collapsed ? 'lg:grid-cols-[72px_1fr]' : 'lg:grid-cols-[260px_1fr]',
      )}
    >
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col border-e border-border bg-surface sticky top-0 h-screen">
        <div className={cn(
          'flex items-center gap-3 h-16 border-b border-border',
          collapsed ? 'px-3 justify-center' : 'px-6',
        )}>
          <div className="h-9 w-9 rounded-lg bg-brand-500 grid place-items-center text-white font-semibold shadow-sm shrink-0">
            A
          </div>
          {!collapsed ? (
            <div className="leading-tight min-w-0">
              <div className="text-sm font-semibold truncate">{t('platform.name')}</div>
              <div className="text-xs text-fg-subtle truncate">{t('platform.tagline')}</div>
            </div>
          ) : null}
        </div>
        <nav className={cn(
          'flex-1 text-sm overflow-y-auto',
          collapsed ? 'p-2 space-y-0.5' : 'p-3 space-y-1',
        )}>
          {visibleSections.map((section) => {
            const isOpen = expanded.has(section.id);
            const hasActiveChild = section.items.some((it) =>
              isRouteActive(location.pathname, it.to),
            );
            if (collapsed) {
              // In collapsed mode show only the section icon and its sub-items
              // (no group title). Behaves like the previous flat list.
              return (
                <div key={section.id} className="space-y-0.5">
                  {section.items.map((it) => (
                    <SideLink key={it.to} {...it} collapsed />
                  ))}
                </div>
              );
            }
            return (
              <div key={section.id}>
                <SectionHeader
                  section={section}
                  isOpen={isOpen}
                  isActive={hasActiveChild}
                  onToggle={() => toggleSection(section.id)}
                />
                {isOpen && (
                  <div className="mt-0.5 mb-1 ms-3 ps-3 border-s border-border space-y-0.5">
                    {section.items.map((it) => (
                      <SideLink key={it.to} {...it} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className={cn(
          'border-t border-border text-xs text-fg-subtle',
          collapsed ? 'p-2 space-y-2' : 'p-4 space-y-3',
        )}>
          {!collapsed ? (
            <>
              <div className="space-y-1">
                <div className="font-medium text-fg truncate">{displayName}</div>
                <div className="truncate">{displayEmail}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="w-full gap-2"
              >
                <LogOut className="h-3.5 w-3.5 rtl-mirror" />
                {tLogin('session.signOut')}
              </Button>
              {/* White-label decision (2026-05-14): no provider pill in the customer portal.
                  Solara keeps a single brand face. See [[solara-tickstar-swap]]. */}
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div
                className="h-8 w-8 rounded-full bg-brand-500 grid place-items-center text-white text-xs font-semibold"
                title={displayName}
              >
                {displayInitial}
              </div>
              <button
                type="button"
                onClick={handleSignOut}
                aria-label={tLogin('session.signOut')}
                title={tLogin('session.signOut')}
                className="h-9 w-9 grid place-items-center rounded-md text-fg-muted hover:bg-surface-muted hover:text-danger"
              >
                <LogOut className="h-4 w-4 rtl-mirror" />
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
          title={collapsed ? t('nav.expand') : t('nav.collapse')}
          className="h-9 mx-2 mb-2 rounded-md text-fg-muted hover:bg-surface-muted flex items-center justify-center gap-2"
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4 rtl-mirror" />
          ) : (
            <>
              <ChevronsLeft className="h-4 w-4 rtl-mirror" />
              <span className="text-xs">{t('nav.collapse')}</span>
            </>
          )}
        </button>
      </aside>

      <div className="flex flex-col min-h-screen lg:min-h-full">
        <header className="sticky top-0 z-20 h-14 lg:h-16 border-b border-border bg-surface/85 backdrop-blur supports-[backdrop-filter]:bg-surface/70 flex items-center gap-2 px-4 lg:px-8">
          <div className="lg:hidden h-8 w-8 rounded-lg bg-brand-500 grid place-items-center text-white font-semibold">
            A
          </div>
          <span className="lg:hidden font-semibold">{t('platform.name')}</span>
          <span className="hidden lg:inline text-fg-subtle text-sm">
            {prettyTitleForPath(location.pathname, t)}
          </span>
          <div className="ms-auto flex items-center gap-1">
            <LiveIndicator />
            <NotificationsButton />
            <RoleSwitcher />
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 pb-24 lg:pb-12">
          <Outlet />
        </main>

        {/* Floating action button (mobile only) */}
        {auth.can('invoices.create') ? (
          <button
            type="button"
            onClick={() => navigate('/new')}
            aria-label={t('nav.newInvoice')}
            className="lg:hidden fixed bottom-20 end-4 h-14 w-14 rounded-full bg-brand-500 text-white shadow-lg active:scale-95 transition-transform grid place-items-center"
          >
            <Plus className="h-6 w-6" />
          </button>
        ) : null}

        {/* Bottom tab nav (mobile) */}
        <nav className="lg:hidden fixed inset-x-0 bottom-0 z-10 border-t border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/85">
          <ul className="grid grid-cols-4">
            {bottomItems.map((it) => (
              <BottomTab key={it.to} to={it.to} icon={it.icon} label={it.label} />
            ))}
            <li>
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                className="w-full h-full flex flex-col items-center justify-center gap-1 py-2 text-[11px] text-fg-subtle"
              >
                <ClipboardList className="h-5 w-5" />
                <span>{t('nav.more')}</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <Dialog
        open={moreOpen}
        onOpenChange={setMoreOpen}
        title={t('nav.more')}
        size="sm"
      >
        <MobileMoreContent
          sections={visibleSections}
          activeSectionId={activeSectionId}
          currentPath={location.pathname}
          onClose={() => setMoreOpen(false)}
          onSignOut={handleSignOut}
          signOutLabel={tLogin('session.signOut')}
        />
      </Dialog>
    </div>
  );
}

function SectionHeader({
  section,
  isOpen,
  isActive,
  onToggle,
}: {
  section: NavSection;
  isOpen: boolean;
  isActive: boolean;
  onToggle: () => void;
}) {
  return (
    <NavLink
      to={section.to}
      onClick={() => {
        // Always open the section when its header is clicked. If it was already
        // open and the route doesn't change, this is a no-op visually.
        if (!isOpen) onToggle();
      }}
      end={section.to === '/'}
      className={({ isActive: navActive }) =>
        cn(
          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
          isActive || navActive
            ? 'bg-brand-50 text-brand-800 font-semibold'
            : 'text-fg hover:bg-surface-muted',
        )
      }
    >
      {section.icon}
      <span className="flex-1 truncate text-start">{section.title}</span>
      <button
        type="button"
        onClick={(e) => {
          // Let the user collapse without navigating away from their current page.
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
        aria-label={isOpen ? 'collapse' : 'expand'}
        className="rounded p-0.5 text-fg-muted hover:bg-surface"
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 rtl-mirror" />
        )}
      </button>
    </NavLink>
  );
}

function SideLink({ to, icon, label, collapsed }: NavItem & { collapsed?: boolean }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg transition-colors',
          collapsed ? 'h-10 w-10 mx-auto justify-center' : 'gap-2.5 px-3 py-1.5 text-[13px]',
          isActive
            ? 'bg-brand-50 text-brand-800 font-medium'
            : 'text-fg-muted hover:bg-surface-muted hover:text-fg',
        )
      }
    >
      {icon}
      {!collapsed ? <span className="truncate">{label}</span> : null}
    </NavLink>
  );
}

function MobileMoreContent({
  sections,
  activeSectionId,
  currentPath,
  onClose,
  onSignOut,
  signOutLabel,
}: {
  sections: NavSection[];
  activeSectionId: string | null;
  currentPath: string;
  onClose: () => void;
  onSignOut: () => void;
  signOutLabel: string;
}) {
  // Default the active section open; the user can toggle others.
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(activeSectionId ? [activeSectionId] : []),
  );
  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  return (
    <div className="space-y-1">
      {sections.map((section) => {
        const isOpen = open.has(section.id);
        const childActive = section.items.some((it) => isRouteActive(currentPath, it.to));
        return (
          <div key={section.id} className="rounded-lg border border-border">
            <button
              type="button"
              onClick={() => toggle(section.id)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2.5 text-sm',
                childActive ? 'text-brand-800 font-semibold' : 'text-fg',
              )}
            >
              {section.icon}
              <span className="flex-1 text-start">{section.title}</span>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-fg-muted" />
              ) : (
                <ChevronRight className="h-4 w-4 text-fg-muted rtl-mirror" />
              )}
            </button>
            {isOpen && (
              <ul className="border-t border-border p-1">
                {section.items.map((it) => (
                  <li key={it.to}>
                    <NavLink
                      to={it.to}
                      onClick={onClose}
                      end={it.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm',
                          isActive
                            ? 'bg-brand-50 text-brand-800 font-medium'
                            : 'text-fg-muted hover:bg-surface-muted',
                        )
                      }
                    >
                      {it.icon}
                      <span>{it.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => {
          onClose();
          onSignOut();
        }}
        className="mt-2 w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-fg-muted hover:bg-surface-muted hover:text-danger"
      >
        <LogOut className="h-5 w-5 rtl-mirror" />
        <span>{signOutLabel}</span>
      </button>
    </div>
  );
}

function BottomTab({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <li>
      <NavLink
        to={to}
        end={to === '/'}
        className={({ isActive }) =>
          cn(
            'flex flex-col items-center justify-center gap-1 py-2 text-[11px]',
            'transition-colors',
            isActive ? 'text-brand-600 font-medium' : 'text-fg-subtle',
          )
        }
      >
        {icon}
        <span>{label}</span>
      </NavLink>
    </li>
  );
}

function isRouteActive(currentPath: string, to: string): boolean {
  if (to === '/') return currentPath === '/';
  return currentPath === to || currentPath.startsWith(`${to}/`);
}

function prettyTitleForPath(path: string, t: (k: string) => string): string {
  if (path.startsWith('/invoices/')) return t('nav.invoices');
  if (path.startsWith('/invoices')) return t('nav.invoices');
  if (path.startsWith('/new')) return t('nav.newInvoice');
  if (path.startsWith('/inbox')) return t('nav.smartInbox');
  if (path.startsWith('/approvals')) return t('nav.approvals');
  if (path.startsWith('/templates')) return t('nav.templates');
  if (path.startsWith('/spend')) return t('nav.spend');
  if (path.startsWith('/vat')) return t('nav.vat');
  if (path.startsWith('/recovery-audit')) return t('nav.recoveryAudit');
  if (path.startsWith('/archive')) return t('nav.archive');
  if (path.startsWith('/compliance')) return t('nav.compliance');
  if (path.startsWith('/settings')) return t('nav.settings');
  if (path.startsWith('/profile')) return 'Profile';
  return t('nav.dashboard');
}

function LiveIndicator() {
  const { t } = useI18n();
  return (
    <span
      className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-success/10 text-success px-2 py-1 text-[11px] font-medium"
      title={t('live.tooltip')}
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-success/50 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
      </span>
      {t('live.label')}
    </span>
  );
}
